"use client";
import Link from "next/link";
import Image from "next/image";
import { ArrowRight } from "lucide-react";
import { usePathname } from "next/navigation";
import SuccessStoriesClient from "./SuccessStoriesClient";
import { SuccessStoriesData, CaseStudy } from "@/types/data";
import { useRef, useEffect } from "react";
import { gsap } from "@/lib/gsap";

export default function SuccessStories({
  background,
  data,
}: {
  background?: string;
  data: SuccessStoriesData["content"];
}) {
  const { tagline, title, description, link, caseStudies } = data;
  const pathname = usePathname();
  const isSuccessStoriesPage = pathname?.includes("/success-stories");

  const sectionRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLAnchorElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!sectionRef.current) return;

    const ctx = gsap.context(() => {
      const timeline = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 70%",
          end: "top 45%",
          toggleActions: "play none none reverse",
          once: false,
        },
      });

      timeline
        .fromTo(
          headerRef.current,
          { opacity: 0, x: -30, scale: 0.95, filter: "blur(8px)" },
          {
            opacity: 1,
            x: 0,
            scale: 1,
            duration: 0.8,
            ease: "power2.out",
            filter: "blur(0px)",
          }
        )
        .fromTo(
          headingRef.current,
          { opacity: 0, y: 40, filter: "blur(8px)" },
          {
            opacity: 1,
            y: 0,
            filter: "blur(0px)",
            duration: 1,
            ease: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
          },
          "-=0.9"
        )
        .fromTo(
          descriptionRef.current,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
          },
          "-=0.8"
        )
        .fromTo(
          ctaRef.current,
          { opacity: 0, y: 20, scale: 1 },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 0.8,
            ease: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
          },
          "-=0.7"
        );

      if (carouselRef.current) {
        gsap.fromTo(
          carouselRef.current,
          { opacity: 0, y: 50, scale: 0.94 },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 1,
            ease: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
            scrollTrigger: {
              trigger: carouselRef.current,
              start: "top 75%",
              end: "top 45%",
              toggleActions: "play none none reverse",
              once: false,
            },
          }
        );
      }
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <SuccessStoriesClient
      cardsCount={caseStudies?.length || 0}
      background={background}
    >
      {}
      <div ref={sectionRef} className="container  pt-12 md:pt-16 ">
        <div className={`mb-[14px] flex flex-col gap-[14px]  `}>
          {tagline && (
            <div ref={headerRef} className="flex items-center ">
              <div className="w-5 h-0.5 bg-theme-primary mr-3"></div>
              <p className="text-xs font-semibold text-theme-primary tracking-wider uppercase">
                {tagline}
              </p>
            </div>
          )}

          {title && (
            <h2
              ref={headingRef}
              className={`text-4xl md:text-5xl font-bold leading-tight ${
                background ? "text-theme-secondary" : "text-white"
              }`}
            >
              {title}
            </h2>
          )}

          {description && (
            <p
              ref={descriptionRef}
              className={`text-[18px] leading-[28px] max-w-2xl  ${
                background ? "text-[#4F5051]" : "text-white"
              }`}
            >
              {description}
            </p>
          )}
        </div>
        {link.url && link.text && !isSuccessStoriesPage && (
          <Link
            href={link.url}
            ref={ctaRef}
            className={`${
              background
                ? "border-[#F6842B] text-[#F6842B]"
                : "bg-white/10 border-white/20 text-white"
            } group self-start   inline-flex items-center justify-center px-8 py-4 font-semibold rounded-lg border
               hover:bg-white/10 transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-white/10 relative overflow-hidden`}
          >
            <span
              className={`transition-all duration-300 ${
                background ? "" : "group-hover:text-white"
              }`}
            >
              {link.text}
            </span>
            <ArrowRight className="h-4 w-4 ml-2 transition-all duration-300 group-hover:translate-x-1 group-hover:scale-110" />
            <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-500 ease-out bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
          </Link>
        )}
      </div>

      {}
      <div
        ref={carouselRef}
        className=" mb-12 md:mb-24 lg:mb-12 xl:mb-0 flex gap-8 overflow-x-auto scrollbar-hide justify-start mt-[52px] max-xl:!ml-[24px]  xl:ml-auto  max-xl:!pl-0  xl:!pl-auto max-xl:max-w-[1260px]  
      max-[1300px]:!max-w-[1260px]  max-[1350px]:!max-w-[1270px] max-[1400px]:!max-w-[1300px] max-[1450px]:!max-w-[1330px] max-[1500px]:!max-w-[1350px] max-[1536px]:!max-w-[1380px] 
      max-[1600px]:!max-w-[1400px] max-[1650px]:!max-w-[1430px] max-[1700px]:!max-w-[1450px] max-[1750px]:!max-w-[1480px] max-[1800px]:!max-w-[1490px] max-[1850px]:!max-w-[1520px] max-[1900px]:!max-w-[1550px]  max-[1921px]:!max-w-[1580px]  max-[2500px]:!max-w-[1580px]
      "
      >
        {caseStudies &&
          caseStudies.length > 0 &&
          caseStudies.map((study: CaseStudy, index: number) => (
            <Link
              key={index}
              href={`/success-stories/${study.slug}`}
              className={`cursor-pointer group relative rounded-2xl overflow-hidden flex-shrink-0 
                h-[300px] sm:h-[320px] md:h-[350px] 
                w-[280px] sm:w-[350px] md:w-[420px] lg:w-[500px]  2xl:w-[600px] 
                transition-all duration-300 ease-in-out hover:scale-[1.02]`}
            >
              {study.image && (
                <Image
                  src={study.image}
                  alt={study.imageAlt}
                  fill
                  quality={75}
                  fetchPriority="low"
                  sizes="(max-width: 640px) 280px, (max-width: 768px) 350px, (max-width: 1024px) 420px, (max-width: 1536px) 500px, 600px"
                  className="w-auto h-auto object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                />
              )}
              {study.title && (
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent group-hover:from-black/80 group-hover:via-black/40 transition-colors duration-500"></div>
              )}
              <div className="relative h-full flex flex-col justify-end p-8 opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-y-8 group-hover:translate-y-0">
                <h2 className="text-3xl font-bold mb-2 text-white">
                  {study.title}
                </h2>
                <p className="text-[#D1D5DB] leading-relaxed max-w-sm">
                  {study.description}
                </p>
              </div>
              <div className="absolute top-6 right-6 w-16 h-16 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full flex items-center justify-center text-white font-semibold text-sm opacity-0 group-hover:opacity-100 scale-90 group-hover:scale-100 transition-all duration-300">
                VIEW
              </div>
            </Link>
          ))}
      </div>
    </SuccessStoriesClient>
  );
}
